// biome-ignore-all lint/a11y/useSemanticElements: <div role="group"> wraps the
//   two active-match sub-lists (your-turn ribbon + waiting-on-them). A
//   <fieldset> would imply form-input semantics inappropriate for a
//   read-only list of match cards; role="group" preserves the
//   aria-labelledby association without misleading semantics.
//
// Phase 17 Plan 05 Task 1 — LobbyScreen rebuilt to UI-SPEC §6.1 LobbyA
// composition (token-and-DOM-equivalent to the prototype's rg-explorations.jsx
// LobbyA section). Data-layer plumbing is preserved verbatim from Plan 15-06:
//   - useStore($lobbyData) + initLobbyPolling() (visibility-aware polling)
//   - useStore($onboarding) for friend-invite trigger (DEC-027 step 5)
//   - useStore($activeMatchCount) + useStore($activeMatchCap) — DEC-009
//   - useRouter for tournaments preview routing
//   - Per-mode modal open state (Casual / Ranked / Bot / Friend)
//
// The render layer composes the new Plan 17-02/03/04 primitives (Btn, Chip)
// against the prototype's lobby-topbar + your-turn / waiting / create-match
// surface order. The 5 CONTEXT.md LOCKED sections are preserved: active
// matches (split into your-turn + waiting) → bot practice → create cluster →
// tournaments preview → notifications. The `data-section` attributes that
// the a11y test asserts are kept on the section wrappers.
//
// Inline styles consume only var(--space-*), var(--color-*), var(--radius-*),
// var(--font-size-*) tokens — no raw hex / px literals (POLISH-04 token gate).

import { useStore } from '@nanostores/react'
import { type JSX, useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { useDocumentTitle } from '../../i18n/useDocumentTitle.js'
import { useRouter } from '../../router/router.js'
import {
  $activeMatchCap,
  $activeMatchCount,
} from '../../stores/activeMatchCount.js'
import { $lobbyData, initLobbyPolling } from '../../stores/lobbyData.js'
import {
  $onboarding,
  consumePendingBotPushOptIn,
  markBotSteerShown,
  markCasualLoadoutPreviewShown,
} from '../../stores/onboarding.js'
import { $tutorial } from '../../stores/tutorial.js'
import { Btn } from '../components/Btn.js'
import { Chip } from '../components/Chip.js'
import { FriendInviteSuggestion } from '../friends/FriendInviteSuggestion.js'
import {
  CASUAL_DEFAULT_LOADOUT,
  LoadoutPickerModal,
} from '../match/LoadoutPickerModal.js'
import { PushOptInModal } from '../match/PushOptInModal.js'
import { ActiveMatchCard } from './ActiveMatchCard.js'
import { BotPracticeCta } from './BotPracticeCta.js'
import { BotTierModal } from './BotTierModal.js'
import { CasualEnqueueModal } from './CasualEnqueueModal.js'
import {
  CreateMatchCluster,
  type CreateMatchMode,
} from './CreateMatchCluster.js'
import { FriendCodeModal } from './FriendCodeModal.js'
import { NotificationsBadge } from './NotificationsBadge.js'
import { RankedEnqueueModal } from './RankedEnqueueModal.js'

/** Inline SVG settings cog (24×24) — folk stroke gear, Btn child. */
function SettingsCogIcon(): JSX.Element {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 2.8v2.6M12 18.6v2.6M21.2 12h-2.6M5.4 12H2.8M18.5 5.5l-1.8 1.8M7.3 16.7l-1.8 1.8M18.5 18.5l-1.8-1.8M7.3 7.3L5.5 5.5" />
    </svg>
  )
}

/** Inline SVG friends/people icon (24×24) — folk stroke (prototype ICON.users). */
function FriendsIcon(): JSX.Element {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="9" cy="9" r="3" />
      <path d="M3.5 19c0-3 2.5-5 5.5-5s5.5 2 5.5 5" />
      <path d="M16 7.5a3 3 0 010 5" />
      <path d="M17.5 14c2 .5 3.5 2.3 3.5 5" />
    </svg>
  )
}

export function LobbyScreen(): JSX.Element {
  useDocumentTitle('doc.title.lobby')
  const onboarding = useStore($onboarding)
  const tutorial = useStore($tutorial)
  const lobby = useStore($lobbyData)
  const activeMatchCount = useStore($activeMatchCount)
  const activeMatchCap = useStore($activeMatchCap)
  const router = useRouter()
  const intl = useIntl()

  const [showFriendInvite, setShowFriendInvite] = useState(false)
  const [botTierOpen, setBotTierOpen] = useState(false)
  const [casualOpen, setCasualOpen] = useState(false)
  const [rankedOpen, setRankedOpen] = useState(false)
  const [friendCodeOpen, setFriendCodeOpen] = useState(false)
  // POWERUP-02 — first-casual read-only loadout preview (shown before enqueue).
  const [casualLoadoutPreviewOpen, setCasualLoadoutPreviewOpen] =
    useState(false)
  // FLOW-06 — post-tutorial, pre-first-real-match bot-practice steer (one-shot).
  const [showBotSteer, setShowBotSteer] = useState(false)
  // ARCA-147 — deferred end-of-first-bot-match push opt-in, surfaced here
  // instead of over the match WIN/LOSE/DRAW overlay.
  const [showPushOptIn, setShowPushOptIn] = useState(false)

  // Plan 15-06 — wire visibility-aware lobby polling on mount. Preserved
  // byte-identical from the previous LobbyScreen.
  useEffect(() => {
    const teardown = initLobbyPolling()
    return teardown
  }, [])

  // ARCA-147 — surface the deferred end-of-first-bot-match push opt-in here,
  // off the WinOverlay. MatchScreen sets a one-shot sessionStorage baton on
  // HOME navigation out of a completed bot match; consume it once on mount and
  // show the modal only while the server one-shot (pushOptInShown) is unset.
  // Reading the flag imperatively (not via the reactive store value) keeps
  // this a true mount-once check. The modal's own CTAs persist pushOptInShown
  // (server flag + local mirror), so it never re-fires after being shown.
  useEffect(() => {
    if (consumePendingBotPushOptIn() && !$onboarding.get().pushOptInShown) {
      setShowPushOptIn(true)
    }
  }, [])

  // Plan 08-06 friend-invite trigger (DEC-027 step 5) — preserved verbatim.
  useEffect(() => {
    if (
      onboarding.matchesPlayed >= 3 &&
      !onboarding.friendInviteShown &&
      !showFriendInvite
    ) {
      setShowFriendInvite(true)
    }
  }, [onboarding.matchesPlayed, onboarding.friendInviteShown, showFriendInvite])

  // FLOW-06 — one-shot bot-practice steer for a player who just finished the
  // tutorial and has not yet played a real match. Mirrors the friend-invite
  // trigger; the persistent flag suppresses it after the first show.
  useEffect(() => {
    if (
      tutorial.completed &&
      onboarding.realMatchesPlayed === 0 &&
      !onboarding.botSteerShown &&
      !showBotSteer
    ) {
      setShowBotSteer(true)
      markBotSteerShown()
    }
  }, [
    tutorial.completed,
    onboarding.realMatchesPlayed,
    onboarding.botSteerShown,
    showBotSteer,
  ])

  // POWERUP-02 — on the FIRST casual match, show the read-only loadout preview
  // before enqueue so a brand-new player meets their four power-ups. Gated by
  // the persistent one-shot flag (realMatchesPlayed is not seeded on lobby
  // mount, so the flag is the authority); everyone else enqueues directly.
  const startCasual = (): void => {
    if (
      onboarding.realMatchesPlayed === 0 &&
      !onboarding.casualLoadoutPreviewShown
    ) {
      setCasualLoadoutPreviewOpen(true)
    } else {
      setCasualOpen(true)
    }
  }

  const handleModeSelected = (kind: CreateMatchMode): void => {
    if (kind === 'casual') startCasual()
    else if (kind === 'ranked') setRankedOpen(true)
    else if (kind === 'bot') setBotTierOpen(true)
    else if (kind === 'friend') setFriendCodeOpen(true)
  }

  const activeMatches = lobby?.active_matches ?? []
  const activeTournaments = lobby?.active_tournaments ?? []

  // Split active matches into your-turn vs waiting (UI-SPEC §6.1 LobbyA two
  // sections: ★ YOUR TURN ribbon section + WAITING ON THEM section).
  const yourTurnMatches = activeMatches.filter(
    (m) => m.current_turn_slot === m.your_slot,
  )
  const waitingMatches = activeMatches.filter(
    (m) => m.current_turn_slot !== m.your_slot,
  )

  // POLISH-13/14 — placeholder coin + gem display until the full currency
  // store ships (Phase 19). Renders the same lockup the prototype shows;
  // the values are display-only.
  const coinsDisplay = '1,247'
  const gemsDisplay = '42'

  const settingsLabel = intl.formatMessage({ id: 'lobby.topbar.settings' })
  const friendsLabel = intl.formatMessage({ id: 'lobby.topbar.friends' })
  const openSettings = (): void => router.navigate('/settings')
  const openFriends = (): void => router.navigate('/friends')

  return (
    <>
      <main
        id="main"
        tabIndex={-1}
        className="rg-screen lobby"
        data-screen="lobby"
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          padding: 'var(--space-4)',
          gap: 'var(--space-4)',
          alignItems: 'center',
        }}
      >
        {/* lobby-topbar — currency cap (rune-orb + coin chip + gem chip) +
            settings cog button. UI-SPEC §6.1 row 1. */}
        <header
          className="lobby-topbar"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            padding: 'var(--space-3) var(--space-4)',
            width: '100%',
            maxWidth: 480,
          }}
        >
          <div
            className="currency-cap"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              padding: '0 var(--space-2)',
              height: 36,
              borderRadius: 'var(--radius-pill)',
              background: 'var(--color-surface-mid)',
              border: '3px solid var(--color-surface-stroke)',
            }}
          >
            <span
              className="rune-orb"
              aria-hidden="true"
              style={{
                display: 'inline-block',
                width: 28,
                height: 28,
                borderRadius: 'var(--radius-pill)',
                background: 'var(--color-magic)',
                boxShadow: 'inset 0 0 8px var(--color-cta-primary)',
              }}
            />
            <Chip variant="gold">{coinsDisplay}</Chip>
            <span
              className="cap-divider"
              aria-hidden="true"
              style={{
                width: 2,
                height: 16,
                background: 'var(--color-surface-stroke)',
              }}
            />
            <Chip variant="them">{gemsDisplay}</Chip>
          </div>
          <span style={{ flex: 1 }} />
          <Btn
            kind="them"
            size="sm"
            onClick={openFriends}
            ariaLabel={friendsLabel}
          >
            <FriendsIcon />
          </Btn>
          <Btn
            kind="them"
            size="sm"
            onClick={openSettings}
            ariaLabel={settingsLabel}
          >
            <SettingsCogIcon />
          </Btn>
        </header>

        {/* Section 1 — Active matches (split into your-turn + waiting) */}
        <section
          aria-labelledby="lobby-active-matches-title"
          data-section="active-matches"
          data-screen-region="active-matches"
          style={{
            width: '100%',
            maxWidth: 480,
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-3)',
            padding: 'var(--space-4)',
            backgroundColor: 'var(--color-surface-mid)',
            border: '3px solid var(--color-surface-stroke)',
            borderRadius: 'var(--radius-lg)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <h2
              id="lobby-active-matches-title"
              className="fd"
              style={{
                margin: 0,
                fontFamily: 'Fredoka, sans-serif',
                fontWeight: 700,
                fontSize: 'var(--font-size-20)',
                color: 'var(--color-text-primary)',
              }}
            >
              <FormattedMessage
                id="social.lobby.active_matches.title"
                defaultMessage="Active matches"
              />
            </h2>
            <span
              data-testid="active-match-cap-badge"
              style={{
                fontFamily: 'Fredoka, sans-serif',
                fontWeight: 700,
                fontSize: 'var(--font-size-13)',
                color: 'var(--color-text-primary)',
                backgroundColor: 'var(--color-surface-deep)',
                border: '2px solid var(--color-surface-stroke)',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--space-1) var(--space-3)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              <FormattedMessage
                id="social.lobby.active_match_cap"
                defaultMessage="{count} of {cap} matches in play"
                values={{ count: activeMatchCount, cap: activeMatchCap }}
              />
            </span>
          </div>

          {yourTurnMatches.length > 0 ? (
            <div
              className="your-turn-section"
              role="group"
              aria-labelledby="your-turn-heading"
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-2)',
              }}
            >
              <span
                className="ribbon yellow"
                id="your-turn-heading"
                style={{
                  // UI-2 fix: keep ONLY layout-specific style (alignSelf for
                  // the flex parent). All visual properties (background,
                  // padding, font, color, border-radius) come from the
                  // .ribbon + .ribbon.yellow recipe in pieces.css so the
                  // hex clip-path renders. Previously inline overrides
                  // collapsed the ribbon to a flat pill chip.
                  alignSelf: 'flex-start',
                }}
              >
                <FormattedMessage
                  id="social.lobby.active_match.your_turn_ribbon"
                  defaultMessage="★ YOUR TURN · {count}"
                  values={{ count: yourTurnMatches.length }}
                />
              </span>
              <ul
                data-testid="active-match-list"
                style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--space-2)',
                }}
              >
                {yourTurnMatches.map((match) => (
                  <li key={match.match_id} style={{ width: '100%' }}>
                    <ActiveMatchCard match={match} />
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {waitingMatches.length > 0 ? (
            <div
              className="waiting-section"
              role="group"
              aria-labelledby="waiting-heading"
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-2)',
              }}
            >
              <h3
                id="waiting-heading"
                className="section-label"
                style={{
                  margin: 0,
                  fontFamily: 'Manrope, system-ui, sans-serif',
                  fontWeight: 600,
                  fontSize: 'var(--font-size-12)',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'var(--color-text-secondary)',
                }}
              >
                <FormattedMessage
                  id="social.lobby.waiting_on_them"
                  defaultMessage="WAITING ON THEM · {count}"
                  values={{ count: waitingMatches.length }}
                />
              </h3>
              <ul
                style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--space-2)',
                }}
              >
                {waitingMatches.map((match) => (
                  <li key={match.match_id} style={{ width: '100%' }}>
                    <ActiveMatchCard match={match} />
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {activeMatches.length === 0 ? (
            <div
              data-testid="lobby-empty-state"
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                gap: 'var(--space-2)',
                padding: 'var(--space-5) var(--space-4)',
              }}
            >
              {/* Folk empty state — a floating Leaf sprout (prototype
                  EmptyMatches Icon.sprout). Float gated under reduced motion
                  via prefers-reduced-motion + [data-motion="reduced"]. */}
              <style>{`
                @keyframes lobby-sprout-float {
                  0%, 100% { transform: translateY(0); }
                  50%      { transform: translateY(-6px); }
                }
                .lobby-empty-sprout {
                  display: inline-flex;
                  animation: lobby-sprout-float 3.4s ease-in-out infinite;
                }
                @media (prefers-reduced-motion: reduce) {
                  .lobby-empty-sprout { animation: none; }
                }
                [data-motion="reduced"] .lobby-empty-sprout { animation: none; }
              `}</style>
              <span aria-hidden="true" className="lobby-empty-sprout">
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="var(--color-accent-leaf)"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  {/* folk sprout (prototype ICON.sprout, Leaf stroke) */}
                  <path d="M12 21V10" />
                  <path d="M12 13c-3 0-5-1.6-5-5 3 0 5 1.6 5 5zM12 11c3-.7 4.6-3 4.6-6C13.6 5 12 7.3 12 11z" />
                </svg>
              </span>
              <p
                style={{
                  margin: 0,
                  fontFamily: 'Fredoka, sans-serif',
                  fontWeight: 700,
                  fontSize: 'var(--font-size-16)',
                  color: 'var(--color-text-primary)',
                }}
              >
                <FormattedMessage
                  id="social.lobby.empty.title"
                  defaultMessage="No matches yet"
                />
              </p>
              <p
                style={{
                  margin: 0,
                  fontFamily: 'Manrope, system-ui, sans-serif',
                  fontWeight: 500,
                  fontSize: 'var(--font-size-14)',
                  color: 'var(--color-text-secondary)',
                }}
              >
                <FormattedMessage
                  id="social.lobby.empty.body"
                  defaultMessage="Tap a mode below to start your first match."
                />
              </p>
            </div>
          ) : null}
        </section>

        {/* Section 2 — Bot-practice CTA */}
        <section
          aria-label={intl.formatMessage({
            id: 'social.lobby.bot_practice_aria',
          })}
          data-section="bot-practice-cta"
          data-screen-region="bot-practice-cta"
          style={{ width: '100%', maxWidth: 480 }}
        >
          <BotPracticeCta
            onClick={() => setBotTierOpen(true)}
            steer={showBotSteer}
            authed
          />
        </section>

        {/* Section 3 — Create-match cluster */}
        <section
          aria-labelledby="lobby-create-match-title"
          data-section="create-match-cluster"
          data-screen-region="create-match-cluster"
          style={{
            width: '100%',
            maxWidth: 480,
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-3)',
            padding: 'var(--space-4)',
            backgroundColor: 'var(--color-surface-mid)',
            border: '3px solid var(--color-surface-stroke)',
            borderRadius: 'var(--radius-lg)',
          }}
        >
          <h2
            id="lobby-create-match-title"
            className="fd"
            style={{
              margin: 0,
              fontFamily: 'Fredoka, sans-serif',
              fontWeight: 700,
              fontSize: 'var(--font-size-20)',
              color: 'var(--color-text-primary)',
            }}
          >
            <FormattedMessage
              id="social.lobby.create_match.title"
              defaultMessage="Start a match"
            />
          </h2>
          <CreateMatchCluster onModeSelected={handleModeSelected} />
        </section>

        {/* Section 4 — Tournaments preview */}
        <section
          aria-labelledby="lobby-tournaments-preview-title"
          data-section="tournaments-preview"
          data-screen-region="tournaments-preview"
          style={{
            width: '100%',
            maxWidth: 480,
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-3)',
            padding: 'var(--space-4)',
            backgroundColor: 'var(--color-surface-mid)',
            border: '3px solid var(--color-surface-stroke)',
            borderRadius: 'var(--radius-lg)',
          }}
        >
          <h2
            id="lobby-tournaments-preview-title"
            className="fd"
            style={{
              margin: 0,
              fontFamily: 'Fredoka, sans-serif',
              fontWeight: 700,
              fontSize: 'var(--font-size-20)',
              color: 'var(--color-text-primary)',
            }}
          >
            <FormattedMessage
              id="social.lobby.tournaments_preview.title"
              defaultMessage="Tournaments"
            />
          </h2>
          {activeTournaments.length === 0 ? (
            <p
              data-testid="tournaments-preview-empty"
              style={{
                margin: 0,
                fontFamily: 'Manrope, system-ui, sans-serif',
                fontWeight: 500,
                fontSize: 'var(--font-size-14)',
                color: 'var(--color-text-secondary)',
              }}
            >
              <FormattedMessage
                id="social.tournaments.empty.body"
                defaultMessage="Join by code to start playing."
              />
            </p>
          ) : (
            <ul
              data-testid="tournaments-preview-list"
              style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-2)',
              }}
            >
              {activeTournaments.slice(0, 3).map((tournament) => (
                <li key={tournament.tournament_id}>
                  <button
                    type="button"
                    onClick={() => router.navigate('/tournaments')}
                    style={{
                      width: '100%',
                      minHeight: 48,
                      padding: 'var(--space-3) var(--space-4)',
                      textAlign: 'left',
                      backgroundColor: 'var(--color-surface-deep)',
                      color: 'var(--color-text-primary)',
                      border: '2px solid var(--color-surface-stroke)',
                      borderRadius: 'var(--radius-md)',
                      fontFamily: 'Fredoka, sans-serif',
                      fontWeight: 700,
                      fontSize: 'var(--font-size-14)',
                      cursor: 'pointer',
                    }}
                  >
                    {tournament.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Section 5 — Notifications badge (top-right surface in header, but
            the existing data-section landmark is kept here for the a11y
            assertion + e2e selector contract). */}
        <section
          aria-label={intl.formatMessage({
            id: 'social.lobby.notifications_section_aria',
          })}
          data-section="notifications"
          data-screen-region="notifications"
          style={{ width: '100%', maxWidth: 480 }}
        >
          <NotificationsBadge />
        </section>

        {/* ARCA-130 — the redundant "+ NEW MATCH" primary CTA was removed.
            Its handler was literally startCasual(), the same path Section 3's
            Casual tile runs, so it offered no differentiation and sat orphaned
            below the notifications section. Section 3 (CreateMatchCluster) is
            now the sole match-creation surface. */}

        {/* Plan 08-06 placeholder body — preserved for the e2e suite's
            "Matches played: N" assertion. */}
        <p
          aria-live="polite"
          data-testid="lobby-matches-played"
          style={{
            fontFamily: 'Manrope, system-ui, sans-serif',
            fontWeight: 500,
            fontSize: 'var(--font-size-12)',
            color: 'var(--color-text-secondary)',
            margin: 0,
          }}
        >
          Matches played: {onboarding.matchesPlayed}
        </p>
      </main>
      <FriendInviteSuggestion
        open={showFriendInvite}
        onClose={() => setShowFriendInvite(false)}
      />
      <BotTierModal open={botTierOpen} onClose={() => setBotTierOpen(false)} />
      {/* POWERUP-02 — first-casual read-only loadout preview. Lock-in proceeds
          to enqueue; Cancel aborts. Either way the one-shot flag is set so it
          never re-appears. */}
      <LoadoutPickerModal
        open={casualLoadoutPreviewOpen}
        mode="casual"
        availablePowerups={CASUAL_DEFAULT_LOADOUT}
        titleId="match.loadout.casual_preview.title"
        bodyId="match.loadout.casual_preview.body"
        ctaLabelId="match.loadout.cta.to_match"
        onLockIn={() => {
          markCasualLoadoutPreviewShown()
          setCasualLoadoutPreviewOpen(false)
          setCasualOpen(true)
        }}
        onCancel={() => {
          markCasualLoadoutPreviewShown()
          setCasualLoadoutPreviewOpen(false)
        }}
      />
      <CasualEnqueueModal
        open={casualOpen}
        onClose={() => setCasualOpen(false)}
      />
      <RankedEnqueueModal
        open={rankedOpen}
        onClose={() => setRankedOpen(false)}
      />
      <FriendCodeModal
        open={friendCodeOpen}
        onClose={() => setFriendCodeOpen(false)}
      />
      {/* ARCA-147 — deferred push opt-in (end of first bot match). Surfaced
          here rather than over the match result overlay. The modal's CTAs
          persist the `pushOptInShown` one-shot, so it shows at most once. */}
      <PushOptInModal
        open={showPushOptIn}
        onDismiss={() => setShowPushOptIn(false)}
      />
    </>
  )
}
